import React, { useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { router } from 'expo-router';
import Text from '@/components/Typography/Text';
import { SettingsCardGroup, SettingsCardRow } from './SettingsCards';
import useResponsiveness from '@/hooks/useResponsiveness';
import PersonalizationSettings from './PersonalizationSettings';
import AiSharingSettings from './AiSharingSettings';
import UsageAnalyticsSettings from './UsageAnalyticsSettings';
import { requiresAiSharingConsent } from '@/lib/appDistribution';
import MacNotificationSettings from './MacNotificationSettings';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { LANGUAGES } from '@/constants/translations';
import { useUIStore, useChatStore, useAuthStore, useModalStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { authApi, accountApi } from '@/lib/api';
import { useTranslation, selectLanguage } from '@/hooks/useTranslation';
import Button from '@/components/Buttons/Button';
import SwitchInput from '@/components/Inputs/Switch';
import { previewWorkNotificationSound } from '@/lib/workNotificationSound';
import Badge from '@/components/Typography/Badge';
import { exportAccountData } from '@/constants/utils/accountDataExport';
import { useFileStore } from '@/store/file';

const styles = StyleSheet.create({
  actions: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10 },
  nativeSection: { paddingHorizontal: 20 },
});

function GeneralSettings() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const buttonSize = isXS ? 'large' : 'medium';
  const workNotificationSounds = useUIStore(state => state.workNotificationSounds);
  const setWorkNotificationSounds = useUIStore(state => state.setWorkNotificationSounds);
  const showMessageReactions = useUIStore(state => state.showMessageReactions !== false);
  const setShowMessageReactions = useUIStore(state => state.setShowMessageReactions);
  const language = useUIStore(state => state.language);
  const clearSession = useAuthStore(state => state.clearSession);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isEmailVerified = useAuthStore(state => state.user?.flags?.isEmailVerified);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [verifyEmailError, setVerifyEmailError] = useState(null);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const clearChats = useChatStore(state => state.clearChats);
  const archiveAllChats = useChatStore(state => state.archiveAllChats);
  const unarchiveAllChats = useChatStore(state => state.unarchiveAllChats);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isExportingAccountData, setIsExportingAccountData] = useState(false);
  const [deletingFilesScope, setDeletingFilesScope] = useState(null);
  const deleteAllFiles = useFileStore(state => state.actions.deleteAllFiles);

  const { useInputModal, useConfirmationModal } = useModalHelper();
  const showInputModal = useInputModal();
  const showConfirmationModal = useConfirmationModal();

  const handleDownloadApps = () => {
    openModal('DownloadAppsModal');
  };

  const handleArchiveAll = () => {
    if (!isPlus) {
      openModal('PlusFeatureModal', { feature: 'archive' });
      return;
    }

    showInputModal({
      title: t('settings.general.dangerZone.archiveAll.confirmTitle'),
      subtitle: t('settings.general.dangerZone.archiveAll.confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: t('settings.general.dangerZone.archiveAll.confirmTitle'),
      type: 'password',
      onSave: async (password) => {
        try {
          setIsProcessing(true);
          const response = await accountApi.archiveAllConversations(password);
          if (!response.error) {
            archiveAllChats();
            addToast(t('settings.general.allArchivedSuccess'), 'success');
          }
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleUnarchiveAll = () => {
    showInputModal({
      title: t('settings.general.dangerZone.unarchiveAll.confirmTitle'),
      subtitle: t('settings.general.dangerZone.unarchiveAll.confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: t('settings.general.dangerZone.unarchiveAll.confirmTitle'),
      type: 'password',
      onSave: async (password) => {
        try {
          setIsProcessing(true);
          const response = await accountApi.unarchiveAllConversations(password);
          if (!response.error) {
            unarchiveAllChats();
            addToast(t('settings.general.allUnarchivedSuccess'), 'success');
          }
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleDeleteAll = () => {
    if (!isPlus) {
      openModal('PlusFeatureModal', { feature: 'delete' });
      return;
    }

    showInputModal({
      title: t('settings.general.dangerZone.deleteAll.confirmTitle'),
      subtitle: t('settings.general.dangerZone.deleteAll.confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: t('settings.general.dangerZone.deleteAll.confirmTitle'),
      type: 'password',
      onSave: async (password) => {
        try {
          setIsProcessing(true);
          const response = await accountApi.deleteAllConversations(password);
          if (!response.error) {
            clearChats();
            addToast(t('settings.general.allDeletedSuccess'), 'success');
          }
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    });
  };

  const handleDeleteFiles = (scope) => {
    const translationKey = scope === 'generated' ? 'deleteGeneratedFiles' : 'deleteFiles';
    const text = key => t(`settings.general.dangerZone.${translationKey}.${key}`);
    showInputModal({
      title: text('title'),
      subtitle: text('confirmMessage'),
      placeholder: t('settings.general.enterPassword'),
      primaryButtonTitle: text('title'),
      type: 'password',
      onSave: async (password) => {
        try {
          setDeletingFilesScope(scope);
          await deleteAllFiles(password, scope);
          addToast(text('success'), 'success');
        } catch (error) {
          addToast(error.message || t('settings.general.errors.operationFailed'), 'error');
        } finally {
          setDeletingFilesScope(null);
        }
      },
    });
  };

  const handleDeleteAccount = () => openModal('DeleteAccountModal');

  const handleExportAccountData = async () => {
    if (isExportingAccountData) return;
    setIsExportingAccountData(true);
    try {
      await exportAccountData();
      const successMessage = Platform.OS === 'web'
        ? t('settings.general.dataExport.successToast')
        : t('settings.general.dataExport.successToastNative');
      addToast(successMessage, 'success');
      // Truncation notes are surfaced inside the archive's README rather
      // than as a follow-up toast; keeps the UI quiet for the common case
      // where nothing was capped.
    } catch (error) {
      addToast(error?.message || t('settings.general.dataExport.errorToast'), 'error');
    } finally {
      setIsExportingAccountData(false);
    }
  };

  const handleSendVerificationEmail = async () => {
    setIsVerifyingEmail(true);
    setVerifyEmailError(null);
    try {
      const response = await authApi.resendVerificationEmail();
      if (!response.error) {
        addToast(t('settings.general.verificationEmailSent'), 'success');
      } else {
        addToast(response.message || t('settings.general.verificationEmailError'), 'error');
      }
    } catch (error) {
      setVerifyEmailError(error.message || t('settings.general.verificationEmailError'));
      addToast(error.message || t('settings.general.verificationEmailError'), 'error');
      console.error('Error sending verification email:', error);
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleLogout = () => {
    showConfirmationModal({
      title: t('settings.general.logout.confirmTitle'),
      message: t('settings.general.logout.confirmMessage'),
      confirmText: t('settings.general.logout.confirmTitle'),
      isDangerous: true,
      onConfirm: async () => {
        await clearSession();
        setTimeout(() => {
          router.replace({ pathname: '/', params: { transition: 'fade_from_bottom' } });
        }, 100);
      }
    });
  };

  return (
    <View testID="general-settings">
      {Platform.OS === 'web' && <SettingsCardGroup>
        <SettingsCardRow isFirst title={t('settings.general.design.appsTitle')} subtitle={t('settings.general.design.appsHelp')}>
          <Button text={t('settings.general.design.downloadApps')} variant="secondary" size={buttonSize}
            prependIcon="arrow-down-to-line" onPress={handleDownloadApps} />
        </SettingsCardRow>
      </SettingsCardGroup>}

      <SettingsCardGroup title={t('settings.general.design.preferences')}>
        <SettingsCardRow isFirst compact title={t('settings.general.language.title')}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>
              <Button text={Object.values(LANGUAGES).find(lang => lang.value === language)?.label || language.toUpperCase()}
                accessibilityLabel={t('settings.general.language.title')}
                variant="secondary" size={buttonSize} appendIcon="chevron-down" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent">
              {Object.values(LANGUAGES).map(lang => (
                <DropdownMenu.CheckboxItem key={lang.value} className="DropdownMenuCheckboxItem"
                  value={language === lang.value ? 'on' : 'off'}
                  onValueChange={() => { selectLanguage(lang.value).catch(() => addToast(t('settings.general.errors.operationFailed'), 'error')); }}>
                  <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">{lang.label}</DropdownMenu.ItemTitle>
                  <DropdownMenu.ItemIndicator />
                </DropdownMenu.CheckboxItem>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </SettingsCardRow>
        {Platform.OS === 'web' && <SettingsCardRow title={t('settings.general.notificationSounds.title')} subtitle={t('settings.general.notificationSounds.subtitle')}>
          <View style={styles.actions}>
            <Button text={t('settings.general.notificationSounds.preview')} variant="transparent" size={buttonSize}
              prependIcon="volume" isDisabled={workNotificationSounds === false} onPress={previewWorkNotificationSound} />
            <SwitchInput accessibilityLabel={t('settings.general.notificationSounds.title')}
              value={workNotificationSounds !== false} onValueChange={setWorkNotificationSounds} />
          </View>
        </SettingsCardRow>}
        <SettingsCardRow title={t('settings.general.emojiReactions.title')} subtitle={t('settings.general.emojiReactions.subtitle')}>
          <SwitchInput accessibilityLabel={t('settings.general.emojiReactions.title')}
            value={showMessageReactions} onValueChange={setShowMessageReactions} />
        </SettingsCardRow>
        {Platform.OS === 'macos' && <View style={styles.nativeSection}><MacNotificationSettings /></View>}
      </SettingsCardGroup>

      {!isGhost && <SettingsCardGroup title={t('settings.general.design.conversations')}>
        <PersonalizationSettings embeddedInPage embeddedInGeneral SectionComponent={SettingsCardRow} />
        <SettingsCardRow title={t('settings.general.dangerZone.archiveAll.title')} subtitle={t('settings.general.design.archiveHelp')}
          titleAccessory={!isPlus && <Badge text="PLUS" onPress={handleArchiveAll} size="small" isPlus />}>
          <View style={styles.actions}>
            <Button text={t('settings.general.dangerZone.archiveAll.button')} variant="secondary" size={buttonSize}
              isLoading={isProcessing} isDisabled={isProcessing} onPress={handleArchiveAll} />
            <Button text={t('settings.general.dangerZone.unarchiveAll.button')} variant="transparent" size={buttonSize}
              isDisabled={isProcessing} onPress={handleUnarchiveAll} />
          </View>
        </SettingsCardRow>
      </SettingsCardGroup>}

      {!isGhost && <SettingsCardGroup title={t('settings.general.design.yourData')}>
        <SettingsCardRow isFirst title={t('settings.general.dataExport.title')} subtitle={t('settings.general.design.exportHelp')}>
          <Button text={t('settings.general.dataExport.button')} variant="secondary" size={buttonSize} prependIcon="download"
            isLoading={isExportingAccountData} isDisabled={isExportingAccountData} onPress={handleExportAccountData} />
        </SettingsCardRow>
        <SettingsCardRow title={t('settings.general.dangerZone.deleteAll.title')} subtitle={t('settings.general.design.deleteChatsHelp')}
          titleAccessory={!isPlus && <Badge text="PLUS" onPress={handleDeleteAll} size="small" isPlus />}>
          <Button text={t('settings.general.dangerZone.deleteAll.button')} variant="transparent" size={buttonSize}
            textStyle={{ color: colors.color_destructive }} isLoading={isProcessing} isDisabled={isProcessing} onPress={handleDeleteAll} />
        </SettingsCardRow>
        <SettingsCardRow title={t('settings.general.dangerZone.deleteGeneratedFiles.title')} subtitle={t('settings.general.dangerZone.deleteGeneratedFiles.subtitle')}>
          <Button text={t('settings.general.dangerZone.deleteGeneratedFiles.title')} variant="transparent" size={buttonSize}
            accessibilityLabel={t('settings.general.dangerZone.deleteGeneratedFiles.title')}
            textStyle={{ color: colors.color_destructive }} isLoading={deletingFilesScope === 'generated'} isDisabled={!!deletingFilesScope}
            onPress={() => handleDeleteFiles('generated')} />
        </SettingsCardRow>
        <SettingsCardRow title={t('settings.general.dangerZone.deleteFiles.title')} subtitle={t('settings.general.dangerZone.deleteFiles.subtitle')}>
          <Button text={t('settings.general.dangerZone.deleteFiles.title')} variant="transparent" size={buttonSize}
            accessibilityLabel={t('settings.general.dangerZone.deleteFiles.title')}
            textStyle={{ color: colors.color_destructive }} isLoading={deletingFilesScope === 'all'} isDisabled={!!deletingFilesScope}
            onPress={() => handleDeleteFiles('all')} />
        </SettingsCardRow>
      </SettingsCardGroup>}

      {(Platform.OS === 'ios' || requiresAiSharingConsent()) && <SettingsCardGroup title={t('settings.general.design.privacy')}>
        <View style={styles.nativeSection}><AiSharingSettings /></View>
        <UsageAnalyticsSettings />
      </SettingsCardGroup>}

      <SettingsCardGroup title={t('settings.general.design.account')}>
        {!isGhost && !isEmailVerified && <SettingsCardRow isFirst title={t('settings.general.verifyEmail.title')} subtitle={t('settings.general.verifyEmail.subtitle')}>
          <View style={{ gap: 8 }}>
            <Button text={t('settings.general.verifyEmail.button')} variant="secondary" size={buttonSize}
              isLoading={isVerifyingEmail} isDisabled={isVerifyingEmail} onPress={handleSendVerificationEmail} />
            {!!verifyEmailError && <Text size="xs" color="danger" accessibilityRole="alert">{verifyEmailError}</Text>}
          </View>
        </SettingsCardRow>}
        <SettingsCardRow isFirst={isGhost || isEmailVerified} title={t('settings.general.logout.title')} subtitle={t('settings.general.design.logoutHelp')}>
          <Button text={t('settings.general.logout.button')} variant="secondary" size={buttonSize} onPress={handleLogout} />
        </SettingsCardRow>
        {!isGhost && <SettingsCardRow title={t('settings.general.dangerZone.deleteAccount.title')} subtitle={t('settings.general.design.deleteAccountHelp')}>
          <Button text={t('settings.general.dangerZone.deleteAccount.title')} variant="transparent" size={buttonSize}
            textStyle={{ color: colors.color_destructive }} onPress={handleDeleteAccount} />
        </SettingsCardRow>}
      </SettingsCardGroup>
    </View>
  );
}

export default React.memo(GeneralSettings);
